import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Heart, ArrowLeft, Sparkles, Download, Share2, X, Instagram, Facebook, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme-specific colors
const THEME_COLORS = {
  healing: {
    bg: '#D4A5A0',
    text: '#FFFFFF',
    accent: '#E7C6C2',
    gradient: 'from-[#D4A5A0]/90 via-[#E7C6C2]/60 to-[#D4A5A0]/90'
  },
  strength: {
    bg: '#6C4CA2',
    text: '#FFFFFF',
    accent: '#8B6BB8',
    gradient: 'from-[#6C4CA2]/90 via-[#8B6BB8]/60 to-[#6C4CA2]/90'
  },
  sisterhood: {
    bg: '#8A9B7A',
    text: '#FFFFFF',
    accent: '#AAB49F',
    gradient: 'from-[#8A9B7A]/90 via-[#AAB49F]/60 to-[#8A9B7A]/90'
  },
  empowerment: {
    bg: '#7C9069',
    text: '#FFFFFF',
    accent: '#AAB49F',
    gradient: 'from-[#7C9069]/90 via-[#AAB49F]/60 to-[#7C9069]/90'
  }
};

const ReelSwiper = () => {
  const { theme } = useParams();
  const navigate = useNavigate();
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [showShareModal, setShowShareModal] = useState(false);
  const [userSessionId] = useState(() => {
    let sessionId = localStorage.getItem('user_session_id');
    if (!sessionId) {
      sessionId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('user_session_id', sessionId);
    }
    return sessionId;
  });
  
  const videoRef = useRef(null);
  const quoteCardRef = useRef(null);
  const startX = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch reels
  useEffect(() => {
    fetchReels();
  }, [theme]);

  // Fetch favorites
  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchReels = async () => {
    try {
      setLoading(true);
      const url = theme ? `${API}/reels/by-theme/${theme}` : `${API}/reels`;
      const response = await axios.get(url);
      setReels(response.data);
      
      // Define expected quote counts per theme
      const quoteCount = {
        'healing': 150,
        'empowerment': 150,
        'strength': 100,
        'sisterhood': 100
      };
      
      const expectedCount = quoteCount[theme] || 100;
      
      // If fewer reels exist than quotes available, generate more
      if (response.data.length < expectedCount) {
        toast.info('Loading all whispers for you...');
        await generateInitialReels();
      }
    } catch (error) {
      console.error('Error fetching reels:', error);
      toast.error('Failed to load reels');
    } finally {
      setLoading(false);
    }
  };

  const generateInitialReels = async () => {
    try {
      const themes = theme ? [theme] : ['healing', 'strength', 'sisterhood', 'empowerment'];
      
      // Define quote counts per theme
      const quoteCount = {
        'healing': 150,
        'empowerment': 150,
        'strength': 150,
        'sisterhood': 150
      };
      
      const promises = themes.flatMap(t => 
        // Generate reels based on quote count for each theme
        Array(quoteCount[t] || 100).fill(null).map(() => generateReel(t, false))
      );
      const results = await Promise.all(promises);
      
      // Filter out any failed generations and set the reels directly
      const validReels = results.filter(r => r !== undefined && r !== null);
      if (validReels.length > 0) {
        setReels(validReels);
      }
    } catch (error) {
      console.error('Error generating initial reels:', error);
      toast.error('Failed to generate reels');
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/reels/favorites/${userSessionId}`);
      const favoriteIds = new Set(response.data.map(r => r.id));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const generateReel = async (reelTheme = null, showToast = true) => {
    try {
      setGenerating(true);
      const targetTheme = reelTheme || theme || 'empowerment';
      const response = await axios.post(`${API}/reels/generate`, {
        theme: targetTheme
      });
      
      if (showToast) {
        // Silently add new whisper without toast notification
        setReels(prev => [response.data, ...prev]);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error generating reel:', error);
      if (showToast) {
        toast.error('Failed to generate reel');
      }
    } finally {
      setGenerating(false);
    }
  };
  
  const shuffleToRandomWhisper = () => {
    if (reels.length <= 1) return;
    
    // Pick a random index different from current
    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * reels.length);
    } while (randomIndex === currentIndex && reels.length > 1);
    
    setCurrentIndex(randomIndex);
  };

  const toggleFavorite = async () => {
    if (reels.length === 0) return;
    
    const currentReel = reels[currentIndex];
    try {
      const response = await axios.post(`${API}/reels/favorite`, {
        user_session_id: userSessionId,
        reel_id: currentReel.id
      });
      
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        if (response.data.status === 'added') {
          newFavorites.add(currentReel.id);
          toast.success('Added to favorites ❤️');
        } else {
          newFavorites.delete(currentReel.id);
          toast.success('Removed from favorites');
        }
        return newFavorites;
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite');
    }
  };

  const handleSwipe = (direction) => {
    if (direction === 'left' && currentIndex < reels.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else if (direction === 'right' && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchEnd = (e) => {
    const endX = e.changedTouches[0].clientX;
    const diff = startX.current - endX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleSwipe('left');
      } else {
        handleSwipe('right');
      }
    }
    setIsDragging(false);
  };

  const handleMouseDown = (e) => {
    startX.current = e.clientX;
    setIsDragging(true);
  };

  const handleMouseUp = (e) => {
    const endX = e.clientX;
    const diff = startX.current - endX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleSwipe('left');
      } else {
        handleSwipe('right');
      }
    }
    setIsDragging(false);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };
  
  const shareToSocial = async (platform) => {
    if (!reels[currentIndex]) return;
    
    const quote = reels[currentIndex].quote;
    const category = reels[currentIndex].theme;
    const appUrl = window.location.origin;
    
    const shareText = `"${quote}"\n\n— From SheSafe Haven 💜\nA healing space for women.\n\nGet your daily whispers: ${appUrl}`;
    
    if (platform === 'whatsapp') {
      // WhatsApp share
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
      window.open(whatsappUrl, '_blank');
      toast.success('Opening WhatsApp... 💜');
      setShowShareModal(false);
    } else if (platform === 'pinterest') {
      // Pinterest share - first download image, then share
      toast.info('Creating your Pinterest-ready image...');
      await downloadImageForPinterest();
    } else if (platform === 'native') {
      // Use native share (works on mobile)
      if (navigator.share) {
        try {
          await navigator.share({
            title: `${category.charAt(0).toUpperCase() + category.slice(1)} - SheSafe Haven`,
            text: shareText,
          });
          toast.success('Thank you for sharing healing 💜');
          setShowShareModal(false);
        } catch (error) {
          if (error.name !== 'AbortError') {
            copyToClipboard(shareText);
          }
        }
      } else {
        copyToClipboard(shareText);
      }
    } else if (platform === 'facebook') {
      // Facebook share
      const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appUrl)}&quote=${encodeURIComponent(shareText)}`;
      window.open(fbUrl, '_blank', 'width=600,height=400');
      toast.success('Opening Facebook...');
      setShowShareModal(false);
    } else if (platform === 'twitter') {
      // Twitter/X share
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
      window.open(twitterUrl, '_blank', 'width=600,height=400');
      toast.success('Opening Twitter...');
      setShowShareModal(false);
    } else if (platform === 'linkedin') {
      // LinkedIn share
      const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(appUrl)}`;
      window.open(linkedinUrl, '_blank', 'width=600,height=400');
      toast.success('Opening LinkedIn...');
      setShowShareModal(false);
    } else if (platform === 'copy') {
      copyToClipboard(shareText);
      setShowShareModal(false);
    }
  };
  
  const downloadImageForPinterest = async () => {
    // Download the image first for Pinterest
    await handleDownload();
    // Then open Pinterest create pin page
    setTimeout(() => {
      const pinterestUrl = `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.origin)}&description=${encodeURIComponent('Daily whispers for healing - SheSafe Haven 💜')}`;
      window.open(pinterestUrl, '_blank', 'width=750,height=550');
      toast.success('Image downloaded! Upload it to Pinterest 💜');
      setShowShareModal(false);
    }, 1000);
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Quote copied! Share it with someone who needs it 💜');
  };
  
  const handleDownload = async () => {
    if (!reels[currentIndex]) return;
    
    try {
      toast.info('Creating your beautiful share card...');
      
      // Create a temporary card element for download
      const downloadCard = document.createElement('div');
      downloadCard.style.cssText = `
        position: fixed;
        left: -9999px;
        width: 1080px;
        height: 1350px;
        padding: 80px;
        background: linear-gradient(135deg, ${currentThemeColors.bg} 0%, ${currentThemeColors.accent} 100%);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        box-sizing: border-box;
      `;
      
      // Quote container
      const quoteContainer = document.createElement('div');
      quoteContainer.style.cssText = `
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        padding: 60px;
      `;
      
      // Quote text
      const quoteText = document.createElement('p');
      quoteText.textContent = reels[currentIndex].quote;
      quoteText.style.cssText = `
        font-family: 'Playfair Display', serif;
        font-size: 48px;
        font-weight: 600;
        line-height: 1.6;
        color: white;
        text-align: center;
        text-shadow: 0 4px 20px rgba(0,0,0,0.3);
        margin: 0;
      `;
      
      // Bottom section with branding
      const bottomSection = document.createElement('div');
      bottomSection.style.cssText = `
        width: 100%;
        padding: 40px 60px;
        background: rgba(255,255,255,0.1);
        border-radius: 30px;
        backdrop-filter: blur(10px);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
      `;
      
      // SheSafe Haven branding
      const branding = document.createElement('div');
      branding.style.cssText = `
        display: flex;
        align-items: center;
        gap: 15px;
      `;
      
      const heartIcon = document.createElement('span');
      heartIcon.textContent = '💜';
      heartIcon.style.cssText = 'font-size: 36px;';
      
      const brandName = document.createElement('p');
      brandName.textContent = 'SheSafe Haven';
      brandName.style.cssText = `
        font-family: 'Playfair Display', serif;
        font-size: 42px;
        font-weight: 700;
        color: white;
        margin: 0;
        text-shadow: 0 2px 10px rgba(0,0,0,0.3);
      `;
      
      // Tagline
      const tagline = document.createElement('p');
      tagline.textContent = 'A healing space for women';
      tagline.style.cssText = `
        font-family: 'Poppins', sans-serif;
        font-size: 24px;
        color: rgba(255,255,255,0.9);
        margin: 0;
        text-align: center;
      `;
      
      // Assemble the card
      quoteContainer.appendChild(quoteText);
      branding.appendChild(heartIcon);
      branding.appendChild(brandName);
      bottomSection.appendChild(branding);
      bottomSection.appendChild(tagline);
      downloadCard.appendChild(quoteContainer);
      downloadCard.appendChild(bottomSection);
      document.body.appendChild(downloadCard);
      
      // Generate image
      const canvas = await html2canvas(downloadCard, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true,
        width: 1080,
        height: 1350
      });
      
      // Remove temporary element
      document.body.removeChild(downloadCard);
      
      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const category = reels[currentIndex].theme;
        link.download = `shesafehaven-${category}-${Date.now()}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        
        toast.success('Beautiful share card created! Post it on Instagram, Pinterest, or TikTok 💜');
      });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#2E2536]">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-[#C8B5E0] mx-auto mb-4 animate-pulse" />
          <p className="text-[#EDE8F3] text-lg">Loading your empowerment...</p>
        </div>
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#2E2536]">
        <div className="text-center px-6">
          <Heart className="w-16 h-16 text-[#E7C6C2] mx-auto mb-4" />
          <p className="text-[#EDE8F3] text-xl mb-6">No whispers yet. Let's create some!</p>
          <Button
            data-testid="generate-first-reel-btn"
            onClick={() => generateReel()}
            disabled={generating}
            className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-6 py-3 rounded-full"
          >
            {generating ? 'Creating...' : 'Generate First Whisper'}
          </Button>
        </div>
      </div>
    );
  }

  const currentReel = reels[currentIndex];
  const isFavorited = favorites.has(currentReel.id);
  const currentThemeColors = THEME_COLORS[currentReel.theme] || THEME_COLORS.empowerment;

  return (
    <div 
      className="h-screen w-screen overflow-hidden relative bg-black"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      data-testid="reel-swiper-container"
    >
      {/* Video Background */}
      <video
        ref={videoRef}
        key={currentReel.id}
        src={currentReel.background_video_url}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        loop
        muted
        playsInline
      />
      
      {/* Theme-specific gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-b ${currentThemeColors.gradient}`}></div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-between p-6 text-white">
        {/* Top Bar */}
        <div className="flex items-center justify-between">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          
          <div 
            data-emergency-trigger
            className="backdrop-blur-md px-4 py-2 rounded-full cursor-pointer group relative"
            style={{ backgroundColor: `${currentThemeColors.bg}CC` }}
          >
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-white" fill="white" />
              <span className="text-sm font-medium capitalize text-white">{currentReel.theme}</span>
            </div>
            {/* Emergency exit hint */}
            <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-[#3E324D] text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
                Double-tap for emergency exit
              </div>
            </div>
          </div>
        </div>

        {/* Center Quote */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-4xl mx-auto flex items-center gap-8">
            {/* Quote Text Area - Scrollable */}
            <div className="flex-1 max-h-[70vh] overflow-y-auto pr-4">
              <div ref={quoteCardRef} className="relative">
                {/* Quote Card for Download - Optimized for Social Media */}
                <div className="text-center p-8 rounded-3xl shadow-2xl" style={{ backgroundColor: `${currentThemeColors.bg}F2` }}>
                  {/* Quote Text */}
                  <p className="quote-text text-xl sm:text-2xl lg:text-3xl font-medium leading-relaxed text-white mb-6" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
                    {currentReel.quote}
                  </p>
                  
                  {/* Watermark - SheSafe Haven only */}
                  <div className="flex flex-col items-center gap-2 mt-6 pt-4 border-t border-white/20">
                    <div className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-white" fill="white" />
                      <p className="text-white text-base font-light tracking-wider">SheSafe Haven</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Action buttons - Always Visible */}
            <div className="flex flex-col gap-4 flex-shrink-0">
              <Button
                data-testid="favorite-btn"
                onClick={toggleFavorite}
                variant="ghost"
                size="icon"
                className="rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 h-14 w-14 relative group"
                title={isFavorited ? "Remove from favorites" : "Save to favorites"}
              >
                <Heart
                  className={`w-7 h-7 ${isFavorited ? 'fill-[#E7C6C2] text-[#E7C6C2]' : 'text-white'}`}
                />
                <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-[#3E324D] text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  {isFavorited ? "Remove" : "Save"}
                </div>
              </Button>

              <Button
                data-testid="share-btn"
                onClick={handleShare}
                variant="ghost"
                size="icon"
                className="rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 h-14 w-14 relative group"
                title="Share this whisper"
              >
                <Share2 className="w-6 h-6 text-white" />
                <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-[#3E324D] text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Share
                </div>
              </Button>

              <Button
                data-testid="download-btn"
                onClick={handleDownload}
                variant="ghost"
                size="icon"
                className="rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 h-14 w-14 relative group"
                title="Download as image"
              >
                <Download className="w-6 h-6 text-white" />
                <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-[#3E324D] text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Download
                </div>
              </Button>

              <Button
                data-testid="generate-new-reel-btn"
                onClick={shuffleToRandomWhisper}
                variant="ghost"
                size="icon"
                className="rounded-full backdrop-blur-md hover:opacity-90 h-14 w-14 relative group"
                style={{ backgroundColor: `${currentThemeColors.bg}E6` }}
                title="Shuffle to random whisper"
              >
                <Sparkles className="w-6 h-6 text-white" />
                <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-[#3E324D] text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Shuffle
                </div>
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Counter */}
        <div className="flex items-center justify-start">
          {/* Navigation hint */}
          <div className="flex flex-col gap-2">
            <div className="text-sm text-white/80 font-medium">
              {currentIndex + 1} / {reels.length}
            </div>
            <div className="text-xs text-white/60">
              Swipe left/right
            </div>
          </div>
        </div>
      </div>

      {/* Swipe indicators */}
      {currentIndex < reels.length - 1 && (
        <div className="absolute bottom-1/2 right-8 transform translate-y-1/2 animate-pulse">
          <div className="text-white/50 text-xl">→</div>
        </div>
      )}
      {currentIndex > 0 && (
        <div className="absolute bottom-1/2 left-8 transform translate-y-1/2 animate-pulse">
          <div className="text-white/50 text-xl">←</div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-[#3E324D] to-[#2E2536] rounded-3xl shadow-2xl max-w-md w-full p-6 border-2 border-[#E7C6C2]/30">
            {/* Close button */}
            <button
              onClick={() => setShowShareModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-[#EDE8F3]" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#E7C6C2]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Share2 className="w-8 h-8 text-[#E7C6C2]" />
              </div>
              <h3 className="text-2xl font-bold text-[#EDE8F3] mb-2">
                Share This Whisper
              </h3>
              <p className="text-[#C8B5E0] text-sm">
                Pass this healing forward to someone who needs it
              </p>
            </div>

            {/* Share Options */}
            <div className="space-y-3">
              {/* Download as Image */}
              <button
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-[#6C4CA2] to-[#8B6BB8] hover:opacity-90 text-white p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
              >
                <Download className="w-5 h-5" />
                <span className="font-semibold">Download as Image</span>
              </button>

              {/* Facebook */}
              <button
                onClick={() => shareToSocial('facebook')}
                className="w-full bg-[#1877F2] hover:opacity-90 text-white p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
              >
                <Facebook className="w-5 h-5" />
                <span className="font-semibold">Share to Facebook</span>
              </button>

              {/* Twitter */}
              <button
                onClick={() => shareToSocial('twitter')}
                className="w-full bg-[#1DA1F2] hover:opacity-90 text-white p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="font-semibold">Share to Twitter/X</span>
              </button>

              {/* LinkedIn */}
              <button
                onClick={() => shareToSocial('linkedin')}
                className="w-full bg-[#0A66C2] hover:opacity-90 text-white p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
              >
                <Linkedin className="w-5 h-5" />
                <span className="font-semibold">Share to LinkedIn</span>
              </button>

              {/* WhatsApp */}
              <button
                onClick={() => shareToSocial('whatsapp')}
                className="w-full bg-[#25D366] hover:opacity-90 text-white p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span className="font-semibold">Share to WhatsApp</span>
              </button>

              {/* Pinterest */}
              <button
                onClick={() => shareToSocial('pinterest')}
                className="w-full bg-[#E60023] hover:opacity-90 text-white p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.350-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                </svg>
                <span className="font-semibold">Share to Pinterest</span>
              </button>

              {/* Instagram (Download reminder) */}
              <button
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-[#E7C6C2] to-[#F7E5E5] hover:opacity-90 text-[#3E324D] p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
              >
                <Instagram className="w-5 h-5" />
                <span className="font-semibold">Download for Instagram/TikTok</span>
              </button>

              {/* Copy Link */}
              <button
                onClick={() => shareToSocial('copy')}
                className="w-full bg-[#3E324D] hover:bg-[#4A3B5A] text-[#EDE8F3] p-4 rounded-xl flex items-center justify-center gap-3 transition-all border border-[#C8B5E0]/20"
              >
                <Share2 className="w-5 h-5" />
                <span className="font-semibold">Copy to Clipboard</span>
              </button>

              {/* Native Share (Mobile) */}
              {navigator.share && (
                <button
                  onClick={() => shareToSocial('native')}
                  className="w-full bg-[#7C9069] hover:opacity-90 text-white p-4 rounded-xl flex items-center justify-center gap-3 transition-all"
                >
                  <Share2 className="w-5 h-5" />
                  <span className="font-semibold">More Share Options...</span>
                </button>
              )}
            </div>

            {/* Footer message */}
            <p className="text-[#C8B5E0] text-xs text-center mt-6 italic">
              "When one woman heals, she creates a path for others to follow." — Angie
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelSwiper;